define('ArtworkUpload.PDP.View', [
    'Backbone',
    'Configuration',
    'ArtworkUpload.Model',
    'ArtworkUpload.PendingRegistry',
    'jQuery',
    'underscore',
    'artwork_upload_widget.tpl'
], function (
    Backbone,
    Configuration,
    ArtworkUploadModel,
    ArtworkUploadPendingRegistry,
    jQuery,
    _,
    artwork_upload_widget_tpl
) {
    'use strict';

    // Item options can arrive either as a Backbone.Collection of Models
    // (attributes.cartOptionId) coming from ProductDetails.Full.View's model,
    // or as a plain array of objects (cartOptionId) coming from a plain
    // getItemInfo() snapshot. Normalize both shapes here.
    function optionsToArray(rawOptions) {
        if (!rawOptions) return [];
        if (_.isFunction(rawOptions.toJSON)) return rawOptions.toJSON();
        if (rawOptions.models) return _.map(rawOptions.models, function (m) { return m.toJSON ? m.toJSON() : m; });
        return _.map(rawOptions, function (opt) {
            return (opt && opt.attributes) ? opt.attributes : opt;
        });
    }

    function getItemOptions(item) {
        if (!item) return [];
        var rawOptions = _.isFunction(item.get) ? item.get('options') : item.options;
        return optionsToArray(rawOptions);
    }

    function getItemField(item, field) {
        if (!item) return undefined;
        return _.isFunction(item.get) ? item.get(field) : item[field];
    }

    return Backbone.View.extend({
        template: artwork_upload_widget_tpl,

        events: {
            'change [data-action="artwork-file-input"]': 'onFileSelected',
            'click [data-action="artwork-remove"]': 'onRemove',
            'click [data-action="artwork-add-more"]': 'onReplaceClick',
            'click .artwork-upload-widget-dropzone': 'onReplaceClick'
        },

        cartOptionId: (Configuration.ArtworkUpload && Configuration.ArtworkUpload.cartOptionId) || 'custcol_artwork_file',
        maxFileSizeMb: (Configuration.ArtworkUpload && Configuration.ArtworkUpload.maxFileSizeMb) || 20,
        // Item-level custom field (custitem) holding how many files this
        // specific item allows - set per item in NetSuite. Falls back to
        // ArtworkUpload.defaultMaxFiles (or 1) when the item doesn't have it set.
        maxFilesFieldId: (Configuration.ArtworkUpload && Configuration.ArtworkUpload.maxFilesFieldId) || 'custitem_artwork_max_files',
        defaultMaxFiles: (Configuration.ArtworkUpload && Configuration.ArtworkUpload.defaultMaxFiles) || 1,
        allowedExtensions: ((Configuration.ArtworkUpload && Configuration.ArtworkUpload.allowedExtensions) || 'jpg,jpeg,png,pdf,svg,ai,eps')
            .split(',')
            .map(function (ext) { return ext.trim().toLowerCase(); })
            .filter(Boolean),
        // SVG deliberately excluded: NetSuite has no native file type for it,
        // so it's stored as PLAINTEXT (see ServiceController.js) and gets
        // served with a text Content-Type - browsers refuse to render that as
        // an <img>, even though the underlying content is valid SVG. Treated
        // like PDF/AI/EPS instead (icon + filename, no inline preview).
        imageExtensions: ['jpg', 'jpeg', 'png'],

        initialize: function (options) {
            this.container = options.container;
            var iteminfo = options.iteminfo || {};
            this.item = iteminfo.item || iteminfo;
            this.itemInternalId = getItemField(this.item, 'internalid');

            this.state = {
                isLoading: false,
                errorMessage: '',
                artworks: []
            };

            ArtworkUploadPendingRegistry.setActiveWidget(this.itemInternalId, this);

            this.render();
            this.repositionInPdp();
        },

        remove: function () {
            ArtworkUploadPendingRegistry.clearActiveWidget(this.itemInternalId);
            if (this._repositionObserver) {
                this._repositionObserver.disconnect();
                this._repositionObserver = null;
            }
            return Backbone.View.prototype.remove.apply(this, arguments);
        },

        // The PDP template has no dedicated slot between the SKU and the
        // Quantity input (confirmed against the exported core
        // product_details_full.tpl - only `Product.Options`/`Product.Stock.Info`
        // sit in that gap), so instead of forking that whole template we mount
        // into the existing `cms:item_info` slot and physically relocate our
        // rendered element right before `[data-view="Quantity"]` once it
        // exists in the DOM. A MutationObserver covers the case where
        // Quantity hasn't rendered yet when we do.
        repositionInPdp: function () {
            if (this._repositioned) return;
            var self = this;

            var attempt = function () {
                var $quantity = jQuery('[data-view="Quantity"]').first();
                if (!$quantity.length) return false;

                self.$el.insertBefore($quantity);
                self._repositioned = true;
                if (self._repositionObserver) {
                    self._repositionObserver.disconnect();
                    self._repositionObserver = null;
                }
                return true;
            };

            if (attempt()) return;

            if (window.MutationObserver) {
                this._repositionObserver = new MutationObserver(function () { attempt(); });
                this._repositionObserver.observe(document.body, { childList: true, subtree: true });
            }
        },

        hasArtworkOption: function () {
            var self = this;
            var options = getItemOptions(this.item);
            return !!_.find(options, function (opt) {
                return opt && opt.cartOptionId === self.cartOptionId;
            });
        },

        getMaxFiles: function () {
            var itemMax = getItemField(this.item, this.maxFilesFieldId);
            var parsed = parseInt(itemMax, 10);
            return parsed > 0 ? parsed : this.defaultMaxFiles;
        },

        getExtension: function (filename) {
            var parts = (filename || '').toLowerCase().split('.');
            return parts.length > 1 ? parts.pop() : '';
        },

        isImageExtension: function (extension) {
            return this.imageExtensions.indexOf(extension) !== -1;
        },

        validateFile: function (file) {
            var extension = this.getExtension(file.name);
            if (this.allowedExtensions.indexOf(extension) === -1) {
                return 'File type not allowed. Accepted: ' + this.allowedExtensions.join(', ') + '.';
            }
            var maxBytes = this.maxFileSizeMb * 1024 * 1024;
            if (file.size > maxBytes) {
                return 'File is too large. Maximum size is ' + this.maxFileSizeMb + 'MB.';
            }
            return null;
        },

        syncPendingRegistry: function () {
            ArtworkUploadPendingRegistry.setAll(this.itemInternalId, this.state.artworks);
        },

        onFileSelected: function (e) {
            var input = e.currentTarget;
            var files = input.files ? Array.prototype.slice.call(input.files) : [];
            if (!files.length) return;

            var maxFiles = this.getMaxFiles();
            var remainingSlots = maxFiles - this.state.artworks.length;

            if (remainingSlots <= 0) {
                this.state.errorMessage = 'You can upload up to ' + maxFiles + ' file(s) for this item.';
                input.value = '';
                this.render();
                return;
            }

            if (files.length > remainingSlots) {
                this.state.errorMessage = 'You can only add ' + remainingSlots + ' more file(s) (max ' + maxFiles + ' for this item).';
                input.value = '';
                this.render();
                return;
            }

            for (var i = 0; i < files.length; i++) {
                var validationError = this.validateFile(files[i]);
                if (validationError) {
                    this.state.errorMessage = validationError;
                    input.value = '';
                    this.render();
                    return;
                }
            }

            var self = this;
            this.state.isLoading = true;
            this.state.errorMessage = '';
            this.render();

            // Uploaded one at a time - keeps error handling/partial-failure
            // reporting simple and avoids surprising the ServiceController
            // with concurrent File Cabinet writes.
            var uploadNext = function (index) {
                if (index >= files.length) {
                    self.state.isLoading = false;
                    input.value = '';
                    self.render();
                    return;
                }

                var file = files[index];
                var model = new ArtworkUploadModel();
                model.uploadFile(file).then(function (result) {
                    var extension = self.getExtension(result.name || file.name);
                    self.state.artworks.push({
                        fileId: result.fileId,
                        fileUrl: result.url,
                        fileName: result.name || file.name,
                        isImage: self.isImageExtension(extension)
                    });
                    self.syncPendingRegistry();
                    uploadNext(index + 1);
                }).fail(function (jqXhr) {
                    var errorMsg = 'Upload failed for "' + file.name + '". Please try again.';
                    if (jqXhr && jqXhr.responseJSON) {
                        if (jqXhr.responseJSON.errorMessage) {
                            errorMsg = jqXhr.responseJSON.errorMessage;
                        } else if (jqXhr.responseJSON.errorDetails && jqXhr.responseJSON.errorDetails.length > 0) {
                            errorMsg = jqXhr.responseJSON.errorDetails[0].message;
                        }
                    }
                    self.state.isLoading = false;
                    self.state.errorMessage = errorMsg;
                    input.value = '';
                    self.render();
                });
            };

            uploadNext(0);
        },

        onRemove: function (e) {
            e.preventDefault();
            var index = parseInt(this.$(e.currentTarget).attr('data-index'), 10);
            if (isNaN(index)) return;

            this.state.artworks.splice(index, 1);
            this.state.errorMessage = '';
            this.syncPendingRegistry();
            this.render();
        },

        onReplaceClick: function (e) {
            e.preventDefault();
            this.$('[data-action="artwork-file-input"]').trigger('click');
        },

        // Called by ArtworkUpload.EntryPoint.js when the native Add to Cart
        // flow is blocked because this item requires artwork and none was
        // uploaded yet.
        showBlockedError: function (message) {
            this.state.errorMessage = message;
            this.render();
            if (this.el && this.el.scrollIntoView) {
                this.el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        },

        getContext: function () {
            var maxFiles = this.getMaxFiles();
            return {
                showWidget: this.hasArtworkOption(),
                isLoading: this.state.isLoading,
                errorMessage: this.state.errorMessage,
                artworks: this.state.artworks,
                hasArtworks: this.state.artworks.length > 0,
                canAddMore: this.state.artworks.length < maxFiles,
                maxFiles: maxFiles,
                allowsMultiple: maxFiles > 1,
                itemInternalId: this.itemInternalId,
                acceptAttr: _.map(this.allowedExtensions, function (ext) { return '.' + ext; }).join(',')
            };
        }
    });
});
